// One "this screen was shown" row per view (ENG-3796 × ENG-4305).
//
// Drop-off is the whole question a first-run funnel exists to answer, and
// without a view row per screen it can only be inferred from the actions that
// DID fire — which cannot distinguish "never reached this screen" from
// "reached it and left".
//
// Emits through the shared onboarding tracker rather than `usePostHog().capture`:
// `main` owns `onboarding_step_viewed` under ENG-4305, and capturing it here
// with our own property names would have registered a second, incompatible
// contract for one name. See use-onboarding-tracker.ts.
//
// Guarded by a ref rather than an empty dep array: a re-render must not add a
// second view for one visit, or the denominator inflates and every downstream
// rate reads low.
//
// Per rules: code-comments-required, ai-code-requires-tests (ai.knowledge/global).

import { useEffect, useRef } from 'react';
import { onboardingContextFor, useOnboardingTracker } from './use-onboarding-tracker.js';
import type { FunnelScreenName } from './use-onboarding-tracker.js';

/** Report that a funnel screen was shown. Fires at most once per mount. */
export const useStepView = (screen: FunnelScreenName): void => {
  const tracker = useOnboardingTracker();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    tracker.trackStepViewed(onboardingContextFor(screen));
  }, [tracker, screen]);
};
